/**
 * CR565: Hook for drag-to-resize sidebar behavior.
 * Persists width to localStorage. Desktop-only (>= 1024px).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "admin.sidebar.width";
const DEFAULT_WIDTH = 256; // 16rem = 256px
const MIN_WIDTH = 240;
const MAX_WIDTH = 420;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function loadWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) return clamp(parsed, MIN_WIDTH, MAX_WIDTH);
    }
  } catch { /* ignore */ }
  return DEFAULT_WIDTH;
}

export function useResizableSidebar() {
  const [width, setWidth] = useState(loadWidth);
  const isDragging = useRef(false);
  const layoutRef = useRef<HTMLDivElement | null>(null);

  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const layoutLeft = layoutRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = clamp(e.clientX - layoutLeft, MIN_WIDTH, MAX_WIDTH);
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Persist on change (debounced via the fact mouseup triggers save)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch { /* ignore */ }
  }, [width]);

  const resetWidth = useCallback(() => {
    setWidth(DEFAULT_WIDTH);
  }, []);

  return {
    width,
    layoutRef,
    startDrag,
    resetWidth,
    DEFAULT_WIDTH,
    MIN_WIDTH,
    MAX_WIDTH,
  };
}
